import { createSlice } from "@reduxjs/toolkit";
import {
  getRegionsList,
  getVoyageControlPlatforms,
} from "Network/Core/DashboardModule/dashboard";
import { Platform, Region } from "Types/App";
import { AppDispatch, RootState } from "store";
import { getFeatureFlags } from "Network/Core/DashboardModule/featureFlag";
import { getLocalStorage, setLocalStorage } from "Network/ApiService";
import { ls_vars } from "Library/Constants";

interface InitialStateType {
  isLoading: boolean;
  language: string;
  platforms: Platform[];
  platform: Platform | null;
  regions: Region[];
  region: Region | null;
  featureFlags: any;
  error?: any;
}

const initialState: InitialStateType = {
  // Loading until initializeAction resolves the persisted platform/region against
  // the server lists — prevents pages from firing API calls with a stale platform.
  isLoading: true,
  language: "en",
  platforms: [],
  // Hydrate from localStorage so a page reload keeps the user's selection;
  // initializeAction validates it against the fresh platform list.
  platform: getLocalStorage(ls_vars.PLATFORM),
  regions: [],
  region: null,
  featureFlags: null,
  error: null,
};

// Define the slice with initial state and reducers for whole platform
const slice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    onLoading(state, action) {
      state.isLoading = action?.payload;
    },
    onInitialize(state, action) {
      state.featureFlags = action?.payload.featureFlags;
      state.platforms = action?.payload.platforms;
      state.platform = action?.payload.platform;
      state.regions = action?.payload.regions;
      state.region = action?.payload.region;
      state.isLoading = false;
    },
    setLanguage(state, action) {
      state.language = action?.payload;
    },
    setPlatforms(state, action) {
      state.platforms = action?.payload;
    },
    setPlatform(state, action) {
      state.platform = action?.payload;
    },
    setRegions(state, action) {
      state.regions = action?.payload;
    },
    setRegion(state, action) {
      state.region = action?.payload;
    },
    setFeatureFlags(state, action) {
      state.featureFlags = action?.payload;
    },
    onError(state, action) {
      state.error = action.payload;
      state.isLoading = false;
    },
  },
});

export const {
  onLoading,
  onInitialize,
  setLanguage,
  setPlatforms,
  setPlatform,
  setRegions,
  setRegion,
  setFeatureFlags,
  onError,
} = slice.actions;

export default slice.reducer;

export const getPlatformsAction =
  () => async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      // PLATFORMS
      dispatch(slice.actions.onLoading(true));
      const platformsRes = await getVoyageControlPlatforms();
      const platformsList = platformsRes.data.results
        .sort((a: any, b: any) => a.id - b.id)
        .filter((el: Platform) => !el.isAdminSite);
      const platform = getState().dashboard.platform;
      // Re-resolve against the fresh list so Redux and localStorage always hold
      // the same platform object (falls back to the first one if no longer valid).
      const resolvedPlatform =
        platformsList?.find((el: Platform) => el.code === platform?.code) ||
        platformsList?.[0];
      dispatch(slice.actions.setPlatform(resolvedPlatform));
      setLocalStorage(ls_vars.PLATFORM, resolvedPlatform);
      dispatch(slice.actions.setPlatforms(platformsList));
      dispatch(slice.actions.onLoading(false));
    } catch (err) {
      dispatch(slice.actions.onError(err));
    }
  };

export const selectPlatformAction =
  (platform: Platform) => async (dispatch: AppDispatch) => {
    dispatch(slice.actions.setPlatform(platform));
    setLocalStorage(ls_vars.PLATFORM, platform);
  };

export const selectRegionAction =
  (region: Region) => async (dispatch: AppDispatch) => {
    dispatch(slice.actions.setRegion(region));
    setLocalStorage(ls_vars.REGION_ID, region?.id);
  };

export const initializeAction =
  () => async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      // PLATFORMS
      dispatch(slice.actions.onLoading(true));
      const platformsRes = await getVoyageControlPlatforms();
      const platformsList = platformsRes.data.results
        .sort((a: any, b: any) => a.id - b.id)
        .filter((el: Platform) => !el.isAdminSite);

      // Keep the previously selected platform (Redux state or the persisted
      // localStorage value) when it still exists in the fresh list; otherwise
      // fall back to the first platform. Redux and localStorage must always
      // hold the same value — GET requests read localStorage while the header
      // dropdown and create payloads read Redux.
      const platformCurr =
        getState().dashboard.platform || getLocalStorage(ls_vars.PLATFORM);
      const regionCurr = getState().dashboard.region;

      const resolvedPlatform =
        platformsList?.find(
          (el: Platform) => el.code === platformCurr?.code
        ) || platformsList?.[0];
      setLocalStorage(ls_vars.PLATFORM, resolvedPlatform);

      // REGIONS
      const regionsRes = await getRegionsList();
      const regionsList = regionsRes.data.sort((a: any, b: any) => a.id - b.id);

      const storedRegionId =
        regionCurr?.id ?? getLocalStorage(ls_vars.REGION_ID);
      const resolvedRegion =
        regionsList?.find(
          (el: Region) => el.id === Number(storedRegionId)
        ) || regionsList?.[0];
      setLocalStorage(ls_vars.REGION_ID, resolvedRegion?.id);

      // FEATURE FLAGS
      const featureFlagsRes = await getFeatureFlags();

      // Modified structure to include platform
      const flagsObj: Record<
        string,
        Record<string | number, Record<number, boolean>>
      > = {};
      featureFlagsRes?.data.forEach((flag: any) => {
        const featureCode = flag.feature.code;
        const platformId = flag.platform ?? "default";
        const regionId = flag.region.id;

        // Initialize feature code if not exists
        if (!flagsObj[featureCode]) {
          flagsObj[featureCode] = {};
        }

        // Initialize platform if not exists
        if (!flagsObj[featureCode][platformId]) {
          flagsObj[featureCode][platformId] = {};
        }

        // Set the flag value for this platform and region
        flagsObj[featureCode][platformId][regionId] = flag.enabled;

        // If this is a default (null platform) setting, also store it separately
        if (flag.platform === null) {
          flagsObj[featureCode]["default"] = {
            [regionId]: flag.enabled,
          };
        }
      });

      dispatch(
        slice.actions.onInitialize({
          platforms: platformsList,
          platform: resolvedPlatform,
          regions: regionsList,
          region: resolvedRegion,
          featureFlags: flagsObj,
        })
      );
    } catch (err) {
      dispatch(slice.actions.onError(err));
    }
  };
